import debounce from 'lodash.debounce';
import { batchUndoableActions } from 'Reducers/batchReducer';
import {
  getCurrentAdStateSelectedComponents,
  getSelectedAdstate,
  makeGetSelectedComponentsKeyframes,
  makeGetComponentByKey,
  getSelectBoxOpen,
  getAssetModalOpen,
  getEaselScale,
  getUpdatableKeyframes,
} from 'Selectors';
import { makeCalculateStyle } from 'Selectors/animation';
import { deselectComponents } from './selectionActions';
import { goToTime } from './adStateActions';

export const createOrUpdateKeyframe = (
  { component, style, skipHistory, subType },
) => ({
  type: subType || 'CREATE_OR_UPDATE_KEYFRAME',
  style,
  componentKey: component.key,
  skipHistory,
});

export const replaceKeyframeStyle = (keyframe, style) => ({
  type: 'REPLACE_KEYFRAME_STYLE',
  keyframe,
  style,
});

export const updateKeyframeStyle = (keyframe, style) => ({
  type: 'UPDATE_KEYFRAME_STYLE',
  keyframe,
  style,
});

export const moveComponent = ({ deltaX, deltaY, skipHistory }) => (dispatch, getState) => {
  const easelScale = getEaselScale(getState());

  dispatch({
    type: 'MOVE_COMPONENT',
    deltaX: deltaX / easelScale,
    deltaY: deltaY / easelScale,
    skipHistory,
  });
};

export const rotateComponent = ({ angle, skipHistory }) => ({
  type: 'ROTATE_COMPONENT',
  angle,
  skipHistory,
});

export const resizeComponent = ({ deltaX, deltaY, rotation, cardinal, skipHistory, lockAspect }) => ({
  type: 'RESIZE_COMPONENT',
  deltaX,
  deltaY,
  rotation,
  cardinal,
  skipHistory,
  lockAspect,
});

export const createKeyframe = (componentKey, currentStyle, keyframeTime) => ({
  type: 'CREATE_KEYFRAME',
  componentKey,
  currentStyle,
  keyframeTime,
});

const getDeltaXAndDeltaYFromArrowEvent = (event, combo) => {
  const multiplier = event.shiftKey ? 10 : 1;
  let offsetX = 0;
  let offsetY = 0;

  if (combo.includes('left')) offsetX = -1;
  if (combo.includes('right')) offsetX = 1;
  if (combo.includes('up')) offsetY = -1;
  if (combo.includes('down')) offsetY = 1;

  const deltaX = offsetX * multiplier;
  const deltaY = offsetY * multiplier;
  return { deltaX, deltaY };
};

const moveComponentByArrowsSaveToHistory = debounce(dispatch =>
  dispatch(moveComponent({ deltaX: 0, deltaY: 0, skipHistory: false }))
, 400);

export const moveComponentByArrows = (event, combo) => (dispatch, getState) => {
  const state = getState();
  if (getAssetModalOpen(state) || getSelectBoxOpen(state)) return;
  const deltas = getDeltaXAndDeltaYFromArrowEvent(event, combo);
  dispatch(moveComponent({ ...deltas, skipHistory: true }));
  moveComponentByArrowsSaveToHistory(dispatch);
};

const resizeComponentByArrowsSaveToHistory = debounce(dispatch =>
  dispatch(resizeComponent({ deltaX: 0, deltaY: 0, cardinal: 'SE', skipHistory: false, lockAspect: false }))
, 400);

export const resizeComponentByArrows = (event, combo) => (dispatch, getState) => {
  const state = getState();
  if (getAssetModalOpen(state) || getSelectBoxOpen(state)) return;
  const deltas = getDeltaXAndDeltaYFromArrowEvent(event, combo);
  dispatch(resizeComponent({ ...deltas, cardinal: 'SE', skipHistory: true, lockAspect: false }));
  resizeComponentByArrowsSaveToHistory(dispatch);
};

export const createKeyframeAtGivenTime = (componentKey, keyframeTime) => {
  const calculateStyle = makeCalculateStyle();
  const getComponentByKey = makeGetComponentByKey();
  return (dispatch, getState) => {
    if (keyframeTime < 0) return;
    const state = getState();
    const component = getComponentByKey(state, { componentKey });
    const currentStyle = calculateStyle(state, { component, currentTime: keyframeTime });
    dispatch(batchUndoableActions([deselectComponents(), createKeyframe(componentKey, currentStyle, keyframeTime), goToTime(keyframeTime)]));
  };
};

export const createKeyframesAtCurrentTime = () => {
  const calculateStyle = makeCalculateStyle();

  return (dispatch, getState) => {
    const state = getState();
    const adState = getSelectedAdstate(state);
    const components = getCurrentAdStateSelectedComponents(state);
    const { currentTime } = adState;

    const actions = components.map((component) => {
      const currentStyle = calculateStyle(state, { component, currentTime });
      return createKeyframe(component.key, currentStyle, currentTime);
    });
    dispatch(batchUndoableActions([deselectComponents(), ...actions]));
  };
};

const removeStyle = (baseStyle, newStyle) => {
  const tmpStyle = baseStyle.delete(newStyle);
  return tmpStyle.toJS();
};

const combineStyle = (baseStyle, newStyle) =>
  (baseStyle
    ? Object.assign(baseStyle.toJS(), newStyle)
    : newStyle);

export const createOrUpdateSelectedKeyframes = style =>
  (dispatch, getState) => {
    const selectedComponents = getCurrentAdStateSelectedComponents(getState());
    selectedComponents.forEach(component =>
      dispatch(createOrUpdateKeyframe({ component, style })));
  };

export const updateSelectedKeyframes = style =>
  (dispatch, getState) => {
    const keyframes = getUpdatableKeyframes(getState());
    keyframes.forEach((keyframe) => {
      const updateStyle = combineStyle(keyframe.style, style);
      dispatch(replaceKeyframeStyle(keyframe, updateStyle));
    });
  };

export const addStyleToSelectedComponents = style => (dispatch, getState) => {
  const keyframeLists = makeGetSelectedComponentsKeyframes()(getState());

  const actions = keyframeLists
      .map(list =>
        list.map((keyframe) => {
          const updateStyle = combineStyle(keyframe.style, style);
          return replaceKeyframeStyle(keyframe, updateStyle);
        }))
      .flatten();

  dispatch(batchUndoableActions(actions));
};

export const removeStyleFromSelectedComponents = style =>
  (dispatch, getState) => {
    const keyframeLists = makeGetSelectedComponentsKeyframes()(getState());

    const actions = keyframeLists
      .map(list =>
        list.map((keyframe) => {
          const updateStyle = removeStyle(keyframe.style, style);
          return replaceKeyframeStyle(keyframe, updateStyle);
        }))
      .flatten();

    dispatch(batchUndoableActions(actions));
  };

export const alignComponent = (axis, alignment) => ({
  type: 'ALIGN_COMPONENT',
  axis,
  alignment,
});



// WEBPACK FOOTER //
// ./app/javascript/packs/App/Builder/actions/styleActions.js